import { useCallback, useEffect, useState } from "react";
import api, { fmtARS, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { TrendUp, TrendDown, ArrowsClockwise, CaretDown, CaretRight, Storefront, DownloadSimple, Plus, Minus } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

/**
 * KPI de variación de preparación de pedidos TN.
 *
 * Compara kg pedidos vs kg producidos por ítem de TN dentro del rango seleccionado.
 * Muestra: total kg pedidos, total kg producidos, %variación, $ facturado, $ que
 * "debería haber facturado" y diferencia. Panel colapsable con top-20 pedidos
 * ordenados por variación absoluta.
 */
export default function TnVariacionCard({ fechaDesde, fechaHasta, fmtMoney }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({}); // { [venta_id]: true }
  const [soloConDif, setSoloConDif] = useState(false);
  const [sortBy, setSortBy] = useState("variacion_abs"); // variacion_abs | fecha_desc | fecha_asc | cliente | kg_pedidos_desc | dif_fact_desc
  // Filtro temporal: por fecha de producción (default) o por fecha del pedido TN.
  // Producción permite ver el impacto por día productivo a medida que se hacen ajustes.
  const [filtrarPor, setFiltrarPor] = useState("produccion");
  // Helper: fecha que se muestra/ordena en cada fila. Cuando filtramos por
  // producción, priorizamos la fecha en que se terminó el pedido para que el
  // orden refleje la actividad productiva del día.
  const rowFecha = (r) =>
    (filtrarPor === "produccion" ? (r.fecha_produccion || r.fecha) : r.fecha) || "";
  const toggleRow = (vid) => setExpanded((e) => ({ ...e, [vid]: !e[vid] }));

  // Filtro + orden aplicados client-side
  const filas = (() => {
    let arr = (data?.detalle || []).filter((r) => (soloConDif ? Math.abs(r.variacion_kg || 0) > 0.001 : true));
    const cmp = {
      variacion_abs: (a, b) => Math.abs(b.variacion_kg || 0) - Math.abs(a.variacion_kg || 0),
      fecha_desc:    (a, b) => rowFecha(b).localeCompare(rowFecha(a)),
      fecha_asc:     (a, b) => rowFecha(a).localeCompare(rowFecha(b)),
      cliente:       (a, b) => (a.cliente || "").localeCompare(b.cliente || "", "es"),
      kg_pedidos_desc: (a, b) => (b.kg_pedidos || 0) - (a.kg_pedidos || 0),
      dif_fact_desc: (a, b) => Math.abs(b.diferencia_facturacion || 0) - Math.abs(a.diferencia_facturacion || 0),
    }[sortBy] || (() => 0);
    return [...arr].sort(cmp);
  })();
  const conDifCount = (data?.detalle || []).filter((r) => Math.abs(r.variacion_kg || 0) > 0.001).length;

  const exportarExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/dashboard/tn-variacion-preparacion/export", {
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta, filtrar_por: filtrarPor },
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const fname = m
        ? m[1]
        : `tn_variacion_${(fechaDesde || "").slice(0, 10)}_${(fechaHasta || "").slice(0, 10)}.xlsx`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch (err) {
      toast.error("No se pudo exportar el Excel");
    } finally { setExporting(false); }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/tn-variacion-preparacion", {
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta, filtrar_por: filtrarPor },
      });
      setData(data);
    } catch (_) {
      setData(null);
    } finally { setLoading(false); }
  }, [fechaDesde, fechaHasta, filtrarPor]);

  useEffect(() => { cargar(); }, [cargar]);

  if (!data) {
    return (
      <div className="border border-[#E5E5E5] bg-white rounded-sm p-5 col-span-full text-center text-[13px] text-neutral-400" data-testid="widget-tn-variacion-loading">
        {loading ? "Cargando variación TN…" : "—"}
      </div>
    );
  }

  const vKg = data.variacion_kg || 0;
  const vPct = data.variacion_pct;
  const vFact = data.diferencia_facturacion || 0;
  const toneKg = vKg > 0.01 ? "pos" : vKg < -0.01 ? "neg" : "neutral";
  // Diferencia = debio_facturar - facturado
  //   > 0 → debiste facturar más de lo que facturaste → PÉRDIDA (rojo)
  //   < 0 → facturaste más de lo que debiste → GANANCIA (verde)
  const toneFact = vFact > 0.5 ? "neg" : vFact < -0.5 ? "pos" : "neutral";
  const labelFact = vFact > 0.5 ? "Pérdida por desvíos" : vFact < -0.5 ? "Ganancia por desvíos" : "Diferencia facturación";
  const arrow = vKg >= 0 ? TrendUp : TrendDown;

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm col-span-full" data-testid="widget-tn-variacion">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 p-3 border-b border-[#E5E5E5]"
      >
        {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        <Storefront size={16} weight="bold" className="text-[#6B1F24]" />
        <span className="text-[13px] font-heading font-semibold text-[#1A1A1A]">Variación de preparación · Tienda Nube</span>
        <Badge variant="outline" className="text-[10px] ml-2">{data.items_evaluados} ítems evaluados</Badge>
        {filtrarPor === "pedido" && data.items_pendientes > 0 && (
          <Badge variant="outline" className="text-[10px] text-neutral-500">{data.items_pendientes} pendientes</Badge>
        )}
        {data.items_sin_stock > 0 && (
          <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">{data.items_sin_stock} sin stock (excluidos)</Badge>
        )}
        <div className="ml-auto flex items-center gap-2">
          <div
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 px-2 py-1 border border-neutral-200 rounded-sm bg-white text-[11px] font-mono"
            data-testid="tn-variacion-filtro-fecha"
            title="Filtrar el rango por fecha de producción (terminado_at) o por fecha del pedido TN"
          >
            <span className="text-neutral-500 uppercase tracking-widest text-[9px]">Filtrar por:</span>
            <select
              value={filtrarPor}
              onChange={(e) => setFiltrarPor(e.target.value)}
              className="text-[11px] font-mono bg-transparent border-none outline-none cursor-pointer"
            >
              <option value="produccion">Fecha de producción</option>
              <option value="pedido">Fecha del pedido</option>
            </select>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); exportarExcel(); }}
            disabled={exporting || !data || data.items_evaluados === 0}
            className="text-[11px] h-7 gap-1"
            data-testid="tn-variacion-export-excel"
          >
            <DownloadSimple size={12} weight="bold" />
            {exporting ? "Exportando…" : "Excel"}
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); cargar(); }}
            disabled={loading}
            className="text-[11px] h-7 gap-1"
          >
            <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
            Refrescar
          </Button>
        </div>
      </button>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 p-4">
        <Kpi
          label="Kg pedidos"
          value={`${fmtNum(data.total_kg_pedidos, 3)} kg`}
          sub={`${data.items_evaluados} ítems terminados`}
          icon={Storefront}
          tone="neutral"
        />
        <Kpi
          label="Kg producidos"
          value={`${fmtNum(data.total_kg_producidos, 3)} kg`}
          sub="Efectivamente entregado (excluye sin_stock)"
          icon={Storefront}
          tone="neutral"
        />
        <Kpi
          label="Variación kg"
          value={`${vKg >= 0 ? "+" : ""}${fmtNum(vKg, 3)} kg`}
          sub={vPct == null ? "sin datos" : `${vPct >= 0 ? "+" : ""}${vPct}% s/pedido`}
          icon={arrow}
          tone={toneKg}
          testid="tn-variacion-kg"
        />
        <Kpi
          label={labelFact}
          value={`${(-vFact) >= 0 ? "+" : ""}${fmtMoney ? fmtMoney(-vFact) : fmtARS(-vFact)}`}
          sub={`Facturado ${fmtMoney ? fmtMoney(data.facturado) : fmtARS(data.facturado)} · Debió ${fmtMoney ? fmtMoney(data.debio_facturar) : fmtARS(data.debio_facturar)}`}
          icon={arrow}
          tone={toneFact}
          testid="tn-variacion-fact"
        />
      </div>
      {open && (
        <div className="border-t border-[#E5E5E5] p-3">
          <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
            <div className="text-[11px] uppercase font-mono tracking-widest text-neutral-500">
              Top pedidos por variación absoluta
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label
                className="inline-flex items-center gap-1.5 text-[11px] font-mono cursor-pointer select-none px-2 py-1 border border-neutral-200 rounded-sm bg-white hover:bg-neutral-50"
                data-testid="tn-variacion-sort"
                title="Ordenar el listado"
              >
                <span className="text-neutral-500 uppercase tracking-widest text-[9px]">Ordenar:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-[11px] font-mono bg-transparent border-none outline-none cursor-pointer"
                >
                  <option value="variacion_abs">Variación kg (mayor)</option>
                  <option value="dif_fact_desc">Δ Facturación (mayor)</option>
                  <option value="fecha_desc">Fecha (más reciente)</option>
                  <option value="fecha_asc">Fecha (más antigua)</option>
                  <option value="cliente">Cliente (A→Z)</option>
                  <option value="kg_pedidos_desc">Kg pedidos (mayor)</option>
                </select>
              </label>
              <label
                className="inline-flex items-center gap-2 text-[11px] font-mono cursor-pointer select-none px-2 py-1 border border-neutral-200 rounded-sm bg-white hover:bg-neutral-50"
                data-testid="tn-variacion-filtro-con-dif"
              >
                <input
                  type="checkbox"
                  className="accent-[#6B1F24]"
                  checked={soloConDif}
                  onChange={(e) => setSoloConDif(e.target.checked)}
                />
                <span>Sólo pedidos con diferencia</span>
                <Badge variant="outline" className="text-[9px]">
                  {conDifCount}/{data.detalle.length}
                </Badge>
              </label>
            </div>
          </div>
          <div className="max-h-96 overflow-auto border border-neutral-200 rounded-sm">
            <table className="w-full text-[12px] font-mono">
              <thead className="bg-neutral-50 text-neutral-600 sticky top-0">
                <tr>
                  <th className="text-center px-1 py-1.5 w-8"></th>
                  <th className="text-left px-2 py-1.5 w-20">#TN</th>
                  <th className="text-left px-2 py-1.5">Cliente</th>
                  <th className="text-left px-2 py-1.5 w-24">Fecha</th>
                  <th className="text-right px-2 py-1.5 w-20">Kg pedidos</th>
                  <th className="text-right px-2 py-1.5 w-20">Kg prod.</th>
                  <th className="text-right px-2 py-1.5 w-24">Var kg</th>
                  <th className="text-right px-2 py-1.5 w-24">Facturado</th>
                  <th className="text-right px-2 py-1.5 w-24">Debió fact.</th>
                  <th className="text-right px-2 py-1.5 w-24">Δ Facturación</th>
                </tr>
              </thead>
              <tbody>
                {filas.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-4 text-neutral-500">
                    {soloConDif && data.detalle.length > 0
                      ? "— ningún pedido con diferencia en el rango —"
                      : "— sin datos —"}
                  </td></tr>
                ) : filas.map((r) => {
                  const isExp = !!expanded[r.venta_id];
                  return (
                    <>
                      <tr
                        key={r.venta_id}
                        className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                        onClick={() => toggleRow(r.venta_id)}
                        data-testid={`tn-variacion-row-${r.tn_number}`}
                      >
                        <td className="px-1 py-1 text-center text-neutral-400">
                          {isExp ? <Minus size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                        </td>
                        <td className="px-2 py-1 font-bold text-[#6B1F24]">#{r.tn_number}</td>
                        <td className="px-2 py-1 truncate max-w-[12rem]">{r.cliente || "—"}</td>
                        <td className="px-2 py-1 text-neutral-500" title={filtrarPor === "produccion" ? `Producido: ${(r.fecha_produccion || "—").slice(0,10)} · Pedido: ${(r.fecha || "—").slice(0,10)}` : `Pedido: ${(r.fecha || "—").slice(0,10)}`}>
                          {rowFecha(r).slice(0, 10) || "—"}
                          {filtrarPor === "produccion" && r.fecha_produccion && r.fecha && r.fecha_produccion.slice(0,10) !== (r.fecha || "").slice(0,10) && (
                            <span className="block text-[9px] text-neutral-400">pedido {(r.fecha || "").slice(0,10)}</span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right">{fmtNum(r.kg_pedidos, 3)}</td>
                        <td className="px-2 py-1 text-right">{fmtNum(r.kg_producidos, 3)}</td>
                        <td className={`px-2 py-1 text-right font-bold ${r.variacion_kg > 0 ? "text-emerald-700" : r.variacion_kg < 0 ? "text-red-600" : ""}`}>
                          {r.variacion_kg >= 0 ? "+" : ""}{fmtNum(r.variacion_kg, 3)}
                          {r.variacion_pct != null && <span className="text-[10px] ml-1 text-neutral-400">({r.variacion_pct >= 0 ? "+" : ""}{r.variacion_pct}%)</span>}
                        </td>
                        <td className="px-2 py-1 text-right text-neutral-600">{fmtMoney ? fmtMoney(r.facturado) : fmtARS(r.facturado)}</td>
                        <td className="px-2 py-1 text-right text-neutral-600">{fmtMoney ? fmtMoney(r.debio_facturar) : fmtARS(r.debio_facturar)}</td>
                        <td className={`px-2 py-1 text-right font-bold ${r.diferencia_facturacion > 0 ? "text-red-600" : r.diferencia_facturacion < 0 ? "text-emerald-700" : ""}`}>
                          {r.diferencia_facturacion >= 0 ? "+" : ""}{fmtMoney ? fmtMoney(r.diferencia_facturacion) : fmtARS(r.diferencia_facturacion)}
                        </td>
                      </tr>
                      {isExp && (
                        <tr className="bg-[#FBF7EF]/60 border-t border-neutral-100">
                          <td colSpan={10} className="px-3 py-2">
                            {(r.items || []).length === 0 ? (
                              <div className="text-[11px] text-neutral-400 italic">Sin ítems terminados para desglosar.</div>
                            ) : (
                              <table className="w-full text-[11px] font-mono" data-testid={`tn-variacion-items-${r.tn_number}`}>
                                <thead className="text-neutral-500 uppercase tracking-wider text-[9px]">
                                  <tr>
                                    <th className="text-left px-2 py-1">Producto</th>
                                    <th className="text-right px-2 py-1 w-20">Kg ped.</th>
                                    <th className="text-right px-2 py-1 w-20">Kg prod.</th>
                                    <th className="text-right px-2 py-1 w-20">Var kg</th>
                                    <th className="text-right px-2 py-1 w-24">Precio unit.</th>
                                    <th className="text-right px-2 py-1 w-24">Facturado</th>
                                    <th className="text-right px-2 py-1 w-24">Debió fact.</th>
                                    <th className="text-right px-2 py-1 w-24">Δ</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.items.map((it, i) => (
                                    <>
                                      <tr key={i} className="border-t border-neutral-200/70">
                                        <td className="px-2 py-1 truncate max-w-[16rem]">
                                          {it.es_combo && (
                                            <span className="inline-block text-[8px] font-bold uppercase bg-amber-100 text-amber-800 px-1 py-0.5 rounded-sm mr-1">COMBO</span>
                                          )}
                                          {it.auditado && (
                                            <span
                                              className="inline-block text-[8px] font-bold uppercase bg-sky-100 text-sky-800 px-1 py-0.5 rounded-sm mr-1"
                                              title="Kg corregidos por Auditoría de Salida (peso real)"
                                              data-testid={`tn-var-auditado-${r.tn_number}-${i}`}
                                            >
                                              AUDITADO
                                            </span>
                                          )}
                                          {it.nombre}
                                          {it.sku && <span className="text-neutral-400 text-[9px] ml-1">SKU {it.sku}</span>}
                                        </td>
                                        <td className="px-2 py-1 text-right">{fmtNum(it.kg_pedidos, 3)}</td>
                                        <td className="px-2 py-1 text-right">{fmtNum(it.kg_producidos, 3)}</td>
                                        <td className={`px-2 py-1 text-right font-semibold ${it.variacion_kg > 0 ? "text-emerald-700" : it.variacion_kg < 0 ? "text-red-600" : ""}`}>
                                          {it.variacion_kg >= 0 ? "+" : ""}{fmtNum(it.variacion_kg, 3)}
                                        </td>
                                        <td className="px-2 py-1 text-right text-neutral-500">
                                          {it.es_combo ? <span className="text-[9px] text-amber-700">(individual)</span> : (fmtMoney ? fmtMoney(it.precio_unitario) : fmtARS(it.precio_unitario))}
                                        </td>
                                        <td className="px-2 py-1 text-right text-neutral-600">{fmtMoney ? fmtMoney(it.facturado) : fmtARS(it.facturado)}</td>
                                        <td className="px-2 py-1 text-right text-neutral-600">{fmtMoney ? fmtMoney(it.debio_facturar) : fmtARS(it.debio_facturar)}</td>
                                        <td className={`px-2 py-1 text-right font-semibold ${it.diferencia_facturacion > 0 ? "text-red-600" : it.diferencia_facturacion < 0 ? "text-emerald-700" : ""}`}>
                                          {it.diferencia_facturacion >= 0 ? "+" : ""}{fmtMoney ? fmtMoney(it.diferencia_facturacion) : fmtARS(it.diferencia_facturacion)}
                                        </td>
                                      </tr>
                                      {it.es_combo && (it.combo_desglose || []).map((c, ci) => (
                                        <tr key={`${i}-c${ci}`} className="bg-amber-50/40 text-[10px]" data-testid={`tn-var-combo-comp-${r.tn_number}-${i}-${ci}`}>
                                          <td className="pl-6 pr-2 py-0.5 text-neutral-700 truncate max-w-[14rem]">
                                            <span className="text-amber-700 mr-1">↳</span>{c.nombre}
                                          </td>
                                          <td className="px-2 py-0.5 text-right">{fmtNum(c.kg_esperados, 3)}</td>
                                          <td className="px-2 py-0.5 text-right">{fmtNum(c.kg_producidos, 3)}</td>
                                          <td className={`px-2 py-0.5 text-right ${c.variacion_kg > 0 ? "text-emerald-700" : c.variacion_kg < 0 ? "text-red-600" : ""}`}>
                                            {c.variacion_kg >= 0 ? "+" : ""}{fmtNum(c.variacion_kg, 3)}
                                          </td>
                                          <td className="px-2 py-0.5 text-right text-neutral-500">{fmtMoney ? fmtMoney(c.precio_unitario) : fmtARS(c.precio_unitario)}</td>
                                          <td className="px-2 py-0.5 text-right text-neutral-600">{fmtMoney ? fmtMoney(c.facturado) : fmtARS(c.facturado)}</td>
                                          <td className="px-2 py-0.5 text-right text-neutral-600">{fmtMoney ? fmtMoney(c.debio_facturar) : fmtARS(c.debio_facturar)}</td>
                                          <td className={`px-2 py-0.5 text-right ${c.diferencia_facturacion > 0 ? "text-red-600" : c.diferencia_facturacion < 0 ? "text-emerald-700" : ""}`}>
                                            {c.diferencia_facturacion >= 0 ? "+" : ""}{fmtMoney ? fmtMoney(c.diferencia_facturacion) : fmtARS(c.diferencia_facturacion)}
                                          </td>
                                        </tr>
                                      ))}
                                    </>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, tone = "neutral", testid }) {
  return (
    <div
      className="border border-[#E5E5E5] bg-white p-4 rounded-sm"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest text-[#666] font-mono">{label}</div>
          <div className="font-mono text-xl font-semibold text-[#1A1A1A] mt-1.5">{value}</div>
          {sub && <div className="font-mono text-[10px] text-[#666] mt-1 truncate">{sub}</div>}
        </div>
        <div
          className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ml-2 ${
            tone === "pos" ? "bg-emerald-50 text-emerald-700" :
            tone === "neg" ? "bg-red-50 text-red-700" :
            "bg-[#F5F1E9] text-[#6B1F24]"
          }`}
        >
          <Icon size={16} weight="bold" />
        </div>
      </div>
    </div>
  );
}
